import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericInfoPanel, toast } from '@/components';
import {
    BellSimpleIcon,
    CalendarBlankIcon,
    ClockIcon,
    CopyIcon,
    MapPinIcon,
} from '@phosphor-icons/react';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { formatDate } from '@/utils/date';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import type { FullSource } from '@/types/source';
import type { Event, EventEntityAlertRule } from '@/types/event';
import { getEventTimeRange } from '@/utils/event-time-range';
import { formatCompactDuration } from '@/utils/time';

interface EventInfoPanelProps {
    event: Event & { confidence?: number };
    source: FullSource & { groups?: { name: string }[] };
    alertRule?: EventEntityAlertRule;
    // Events whose length comes from a video range rather than from frames, such
    // as report custom events, whose frames only hold the chosen thumbnail.
    durationSeconds?: number;
}

export function EventInfoPanel({
    event,
    source,
    alertRule,
    durationSeconds,
}: EventInfoPanelProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { alertsEnabled } = useFeatureFlags();

    const infoData = useMemo(() => {
        const { startTime, endTime } = getEventTimeRange(event);
        const rangeDuration =
            startTime !== undefined && endTime !== undefined
                ? endTime - startTime
                : 0;
        const duration = formatCompactDuration(
            durationSeconds ?? rangeDuration,
        );

        return [
            {
                id: 'dateTime',
                label: t('infoPanel.dateTime'),
                value: formatDate(event.createdAt),
                icon: CalendarBlankIcon,
            },
            {
                id: 'source',
                label: t('infoPanel.source'),
                value: source.displayName,
                icon: SecurityCameraIconMirrored,
                link: `/sources/${source.id}`,
            },
            {
                id: 'location',
                label: t('infoPanel.location'),
                value: source.groups?.[0]?.name,
                icon: MapPinIcon,
            },
            {
                id: 'duration',
                label: t('infoPanel.duration'),
                value: duration,
                icon: ClockIcon,
            },
            {
                id: 'alertRule',
                label: t('infoPanel.associatedAlertRule'),
                value: alertRule?.promptText || '',
                icon: BellSimpleIcon,
                ...(alertRule &&
                    alertsEnabled && {
                        link: `/alert-rules/${alertRule.id}`,
                    }),
            },
        ];
    }, [event, source, t, alertRule, alertsEnabled, durationSeconds]);

    const handleCopyEventId = () => {
        navigator.clipboard.writeText(event?.id || '');
        toast.success(t('eventIdCopied'));
    };

    return (
        <GenericInfoPanel
            title={t('infoPanel.eventDetails')}
            className="w-full p-5"
            items={infoData}
            headerContent={
                <button
                    type="button"
                    onClick={handleCopyEventId}
                    className="flex items-center gap-1 text-sm text-grey-200 hover:text-grey-100 transition-colors"
                >
                    {t('infoPanel.copyEventId')}
                    <CopyIcon className="w-4 h-4" />
                </button>
            }
        />
    );
}
